/**
 * CR421 Phase 3B: Simplified hook for managing Rule Packs
 * Single activation model — no environment logic
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type PackType = "NORMALIZE" | "DEDUPE";
export type PackStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";

export interface RulePack {
  id: string;
  name: string;
  version: string;
  status: PackStatus;
  config_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface Activation {
  id: string;
  pack_type: PackType;
  active_pack_id: string;
  activated_at: string;
  activated_by: string | null;
  previous_pack_id: string | null;
  notes: string | null;
}

export interface ActivationAuditEntry {
  id: string;
  pack_type: string;
  active_pack_id: string;
  previous_pack_id: string | null;
  activated_at: string;
  activated_by: string | null;
  notes: string | null;
}

export interface RulePacksData {
  normalize_packs: RulePack[];
  dedupe_packs: RulePack[];
  activations: Record<string, Activation>;
}

export function useRulePacks() {
  const [data, setData] = useState<RulePacksData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result, error } = await supabase.functions.invoke("rule-packs", {
        method: "GET",
      });

      if (error) throw error;
      setData(result);
    } catch (err) {
      console.error("[useRulePacks] Fetch error:", err);
      toast({
        title: "Failed to load rule packs",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const createPack = useCallback(async (
    packType: PackType,
    name: string,
    version: string,
    config: Record<string, unknown>
  ): Promise<RulePack | null> => {
    setSaving(true);
    try {
      const { data: result, error } = await supabase.functions.invoke(
        `rule-packs?pack_type=${packType}`,
        {
          method: "POST",
          body: { name, version, config_json: config },
        }
      );

      if (error) throw error;

      toast({ title: "Rule pack created" });
      await fetchPacks();
      return result?.pack || null;
    } catch (err) {
      console.error("[useRulePacks] Create error:", err);
      toast({
        title: "Failed to create rule pack",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      return null;
    } finally {
      setSaving(false);
    }
  }, [fetchPacks, toast]);

  const updatePack = useCallback(async (
    packType: PackType,
    packId: string,
    updates: { name?: string; version?: string; config_json?: Record<string, unknown> }
  ): Promise<RulePack | null> => {
    setSaving(true);
    const endpoint = `rule-packs?pack_type=${packType}&pack_id=${packId}&action=update`;
    
    try {
      const { data: result, error } = await supabase.functions.invoke(endpoint, {
        method: "POST",
        body: updates,
      });

      if (error) {
        const errorDetail = error.message || JSON.stringify(error);
        throw new Error(errorDetail);
      }

      if (result?.error) {
        const details = result.details ? `: ${result.details.join(", ")}` : "";
        throw new Error(`${result.error}${details}`);
      }

      toast({ title: "Rule pack updated" });
      await fetchPacks();
      return result?.pack || null;
    } catch (err) {
      console.error("[useRulePacks] Update error:", err);
      const message = err instanceof Error ? err.message : "Unknown error";
      toast({
        title: "Failed to update rule pack",
        description: message.slice(0, 200),
        variant: "destructive",
      });
      return null;
    } finally {
      setSaving(false);
    }
  }, [fetchPacks, toast]);

  const clonePack = useCallback(async (
    packType: PackType,
    packId: string,
    newName?: string,
    newVersion?: string
  ): Promise<RulePack | null> => {
    setSaving(true);
    try {
      const { data: result, error } = await supabase.functions.invoke(
        `rule-packs?pack_type=${packType}&pack_id=${packId}&action=clone`,
        {
          method: "POST",
          body: { name: newName, version: newVersion },
        }
      );

      if (error) throw error;

      toast({ title: "Rule pack cloned" });
      await fetchPacks();
      return result?.pack || null;
    } catch (err) {
      console.error("[useRulePacks] Clone error:", err);
      toast({
        title: "Failed to clone rule pack",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      return null;
    } finally {
      setSaving(false);
    }
  }, [fetchPacks, toast]);

  // CR421: Simplified activation — uses RPC, no environment
  const activatePack = useCallback(async (
    packType: PackType,
    packId: string,
    notes?: string
  ): Promise<boolean> => {
    setSaving(true);
    try {
      const { error } = await supabase.rpc("cr421_activate_rule_pack", {
        p_pack_type: packType,
        p_pack_id: packId,
        p_activated_by: null,
        p_notes: notes || null,
      });

      if (error) throw error;

      toast({
        title: `Rule pack activated`,
        description: "This pack will now be used for all runs",
      });
      await fetchPacks();
      return true;
    } catch (err) {
      console.error("[useRulePacks] Activate error:", err);
      toast({
        title: "Failed to activate rule pack",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [fetchPacks, toast]);

  const archivePack = useCallback(async (
    packType: PackType,
    packId: string
  ): Promise<boolean> => {
    setSaving(true);
    const endpoint = `rule-packs?pack_type=${packType}&pack_id=${packId}&action=archive`;
    
    try {
      const { data: result, error } = await supabase.functions.invoke(endpoint, {
        method: "POST",
        body: {},
      });

      if (error) {
        const errorDetail = error.message || JSON.stringify(error);
        throw new Error(errorDetail);
      }

      if (result?.error) {
        throw new Error(result.error);
      }

      toast({ title: "Rule pack archived" });
      await fetchPacks();
      return true;
    } catch (err) {
      console.error("[useRulePacks] Archive error:", err);
      const message = err instanceof Error ? err.message : "Unknown error";
      toast({
        title: "Failed to archive rule pack",
        description: message.slice(0, 200),
        variant: "destructive",
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [fetchPacks, toast]);

  const deletePack = useCallback(async (
    packType: PackType,
    packId: string
  ): Promise<boolean> => {
    setSaving(true);
    const endpoint = `rule-packs?pack_type=${packType}&pack_id=${packId}&action=delete`;
    
    try {
      const { data: result, error } = await supabase.functions.invoke(endpoint, {
        method: "POST",
        body: {},
      });

      if (error) {
        const errorDetail = error.message || JSON.stringify(error);
        throw new Error(errorDetail);
      }

      if (result?.error) {
        throw new Error(result.error);
      }

      toast({ title: "Rule pack deleted permanently" });
      await fetchPacks();
      return true;
    } catch (err) {
      console.error("[useRulePacks] Delete error:", err);
      const message = err instanceof Error ? err.message : "Unknown error";
      toast({
        title: "Failed to delete rule pack",
        description: message.slice(0, 200),
        variant: "destructive",
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [fetchPacks, toast]);

  // CR421: Delete archived packs via RPC
  const deleteArchivedPacks = useCallback(async (
    packType: PackType,
    packIds: string[]
  ): Promise<boolean> => {
    setSaving(true);
    try {
      const { data: result, error } = await supabase.rpc("cr421_delete_archived_rule_packs", {
        p_pack_type: packType,
        p_ids: packIds,
      });

      if (error) throw error;

      toast({ title: `${result || 0} archived pack(s) deleted` });
      await fetchPacks();
      return true;
    } catch (err) {
      console.error("[useRulePacks] Delete archived error:", err);
      toast({
        title: "Failed to delete archived packs",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [fetchPacks, toast]);

  // CR421: Fetch activation history
  const fetchActivationHistory = useCallback(async (): Promise<ActivationAuditEntry[]> => {
    try {
      const { data, error } = await supabase
        .from("rule_pack_activation_audit")
        .select("*")
        .order("activated_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data || []) as ActivationAuditEntry[];
    } catch (err) {
      console.error("[useRulePacks] Fetch history error:", err);
      return [];
    }
  }, []);

  // CR421: Simplified — single active pack per type
  const getActivePack = useCallback((packType: PackType): RulePack | null => {
    if (!data) return null;
    const key = packType;
    const activation = data.activations[key];
    if (!activation) return null;

    const packs = packType === "NORMALIZE" ? data.normalize_packs : data.dedupe_packs;
    return packs.find(p => p.id === activation.active_pack_id) || null;
  }, [data]);

  return {
    data,
    loading,
    saving,
    refetch: fetchPacks,
    createPack,
    updatePack,
    clonePack,
    activatePack,
    archivePack,
    deletePack,
    deleteArchivedPacks,
    fetchActivationHistory,
    getActivePack,
  };
}
